import {
  GoogleAuthProvider,
  type AuthCredential,
  linkWithCredential,
  linkWithPopup,
  onIdTokenChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile,
  type User,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { useCallback, useEffect, useState } from 'react';
import { config } from '../config';
import { auth, callables } from '../firebase';
import { noteTimeZone } from '../lib/visitors';

/** A browser driven by automation, on the live site (the emulators' end-to-end tests still sign in). */
const AUTOMATED = import.meta.env.PROD && navigator.webdriver;

let adopting: Promise<boolean> | null = null;

/**
 * Linking Google to a visitor's account keeps the visitor's empty profile, so the Google name and
 * photo are copied across (posts said "Someone" otherwise). True when the profile changed. Calls
 * made while one is under way share it (linking also fires the token listener).
 */
function adoptGoogleProfile(user: User): Promise<boolean> {
  adopting ??= (async () => {
    const google = user.providerData.find((p) => p.providerId === 'google.com');
    // Only what's missing, and never a null (the API rejects it).
    const update: { displayName?: string; photoURL?: string } = {};
    if (!user.displayName && google?.displayName) update.displayName = google.displayName;
    if (!user.photoURL && google?.photoURL) update.photoURL = google.photoURL;
    if (!update.displayName && !update.photoURL) return false;
    await updateProfile(user, update);
    return true;
  })().finally(() => {
    adopting = null;
  });
  return adopting;
}

/** Links Google to the anonymous visitor (keeping their uid), or switches to an existing account. */
async function linkOrSignIn(
  link: () => Promise<{ user: User }>,
  fallback?: AuthCredential,
): Promise<void> {
  try {
    const { user } = await link();
    await adoptGoogleProfile(user).catch(() => false);
    // Fresh token so the server and rules see the linked Google identity (and name) right away.
    await user.getIdToken(true);
  } catch (err) {
    // This Google account already belongs to another user: switch to it.
    if (err instanceof FirebaseError && err.code === 'auth/credential-already-in-use') {
      const credential = GoogleAuthProvider.credentialFromError(err) ?? fallback;
      if (credential) {
        await signInWithCredential(auth, credential);
        return;
      }
    }
    throw err;
  }
}

// Dev + emulators only (stripped from production builds): lets end-to-end tests sign in as a
// "Google" user without driving the emulator's sign-in popup. The Auth emulator accepts unsigned
// fake Google ID tokens.
if (import.meta.env.DEV && config.useEmulators) {
  const w = window as unknown as {
    __tottTestSignIn?: (name: string, email: string) => Promise<void>;
  };
  w.__tottTestSignIn = async (name, email) => {
    const credential = GoogleAuthProvider.credential(
      JSON.stringify({ sub: email, email, name, email_verified: true }),
    );
    const current = auth.currentUser;
    if (current?.isAnonymous) {
      await linkOrSignIn(() => linkWithCredential(current, credential), credential);
    } else await signInWithCredential(auth, credential);
  };
}

export interface AuthState {
  user: User | null;
  /** True once a real (Google) identity is attached; anonymous visitors can browse and open posts. */
  signedIn: boolean;
  admin: boolean;
  ready: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

interface Snapshot {
  user: User | null;
  signedIn: boolean;
  admin: boolean;
  ready: boolean;
}

/**
 * Everyone gets a Firebase user: anonymous until they sign in with Google. Signing in links Google
 * to the same uid, so anything recorded while browsing stays theirs.
 */
export function useAuth(): AuthState {
  // One snapshot object per token change: Firebase mutates the same User instance when an account
  // is linked, so storing the User alone would not re-render.
  const [snap, setSnap] = useState<Snapshot>(() => ({
    user: auth.currentUser,
    signedIn: !!auth.currentUser && !auth.currentUser.isAnonymous,
    admin: false,
    ready: false,
  }));

  useEffect(
    () =>
      onIdTokenChanged(auth, async (u) => {
        if (!u) {
          // Automated browsers (test runs, most bots) read the board without an account, so they
          // aren't counted as visitors and their clicks aren't counted as opens.
          if (AUTOMATED) {
            setSnap({ user: null, signedIn: false, admin: false, ready: true });
            return;
          }
          setSnap({ user: null, signedIn: false, admin: false, ready: false });
          try {
            await signInAnonymously(auth);
          } catch (err) {
            console.warn('Anonymous sign-in failed', err);
            setSnap({ user: null, signedIn: false, admin: false, ready: true });
          }
          return;
        }
        // People who linked Google before the profile was copied across: copy it now. The token's
        // name and picture come from the profile, so refresh it (this callback then runs again).
        if (!u.isAnonymous && (await adoptGoogleProfile(u).catch(() => false))) {
          void u.getIdToken(true).catch(() => undefined);
        }
        const token = await u.getIdTokenResult();
        setSnap({
          user: u,
          signedIn: !u.isAnonymous,
          admin: token.claims.admin === true,
          ready: true,
        });
      }),
    [],
  );

  // People listed as admins on the server get the admin claim on their first sign-in (once per
  // browser session); a fresh token then brings the admin tools in.
  const uid = snap.user?.uid;
  useEffect(() => {
    if (!snap.signedIn || snap.admin || !uid) return;
    const key = `tott.claimedAdmin.${uid}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* storage unavailable: still try once per mount */
    }
    callables
      .claimAdmin()
      .then((res) => (res.data.admin ? auth.currentUser?.getIdToken(true) : undefined))
      .catch(() => undefined);
  }, [snap.signedIn, snap.admin, uid]);

  // Each browser notes its time zone once, for the admins' visitors-by-country count.
  useEffect(() => {
    if (uid) void noteTimeZone(uid);
  }, [uid]);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const current = auth.currentUser;
    if (current?.isAnonymous) await linkOrSignIn(() => linkWithPopup(current, provider));
    else await signInWithPopup(auth, provider);
  }, []);

  const signOut = useCallback(async () => {
    await fbSignOut(auth); // onIdTokenChanged then signs in anonymously again
  }, []);

  return { ...snap, signInWithGoogle, signOut };
}
